// src/hooks/useOverheardItems.js
//
// Ticket 25 (Overheard on the Shelf). Reads the active rows of
// public.overheard_items: the example cravings the homepage rows drift past and
// the craving box rotates through as placeholders. One list, read once, so the
// two places that show examples cannot disagree.
//
// ORDER. "A fresh selection and order is drawn on every visit" (spec 0e), so the
// rows are shuffled once when they arrive and handed back in that order. The
// page stays put for the visit; a reload draws again. Nothing is stored.
//
// FAILURE IS NOT EMPTY. A failed read returns { items: [], error } and reports
// to Sentry; it does not pretend the table is empty. The reader sees nothing
// extra (the box falls back to its plain placeholder and the rows do not draw),
// which is the right amount of noise for a decoration, but somebody finds out.
//
// RLS lets anyone read active rows, so no sign-in is needed (a guest sees the
// rows). The .eq('is_active', true) is a second statement of the same rule, not
// the thing the rule depends on.
//
// Any row under MIN_CRAVING_WORDS is dropped here as well. The database
// refuses to store one (overheard_items_min_words); this only keeps a bad row
// from ever teaching a reader to write something the box refuses.

import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import { MIN_CRAVING_WORDS, wordsOf } from '../lib/cravingWords';
import { shuffled } from '../lib/shuffle';

export function useOverheardItems() {
  const [state, setState] = useState({ items: [], loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase
        .from('overheard_items')
        .select('id, text, source')
        .eq('is_active', true);
      if (cancelled) return;
      if (error) {
        console.error('useOverheardItems: read failed:', error.message);
        Sentry.captureMessage(`overheard_items read failed: ${error.message}`, 'warning');
        setState({ items: [], loading: false, error: error.message });
        return;
      }
      const usable = (data || []).filter(r => wordsOf(r.text) >= MIN_CRAVING_WORDS);
      setState({ items: shuffled(usable), loading: false, error: null });
    }
    load();
    return () => { cancelled = true; };
  }, []);

  return state;
}
